import { LoadingInfo, LoadingStatusProvider } from '../../application/models/LoadingInfo';
import {
  AssociateInfo,
  Enrollments,
  getDefaultFormData,
  NewJoinSessionData,
  NewMembershipFormData,
  ProductType,
} from '../../application/models/NewJoinSessionData';
import React from 'react';
import { ContentStatus, INewMembershipInfo, IPartnerIntegration, PaperlessOptOutType } from '@lib/types';
import { MembershipStatus } from '../../application/models/MembershipStatus';
import {
  IGetMembershipContentResponse,
  IGetNewBusinessCostResponse,
  IRegisterAccountResponse,
  ISubmitNewJoinFormDataResponse,
  IUpdateCacheResponse,
} from '../../api/contracts';
import { ErrorCode } from '@ace-clublabs/payment-types';
import { CartProductCode, CurrentPayPlan } from '@ace-clublabs/common-membership-api-types';
import { UtagType } from '../../utils/UtagHelper';

export interface IMembershipAppState {
  loadingInfo: LoadingInfo;
  sessionData: NewJoinSessionData;
  loadingProvider: LoadingStatusProvider;
  membershipInfo: INewMembershipInfo | null;
  isPriceCartLoading: boolean;
  setZipCouponCampaignCodeAndFlowType: (
    zipCode: string | null,
    couponToken: string | null,
    campaignCode: string | null,
    flowType: string | null,
    queryString: string | null,
    employeeId: string | null
  ) => void;
  refreshSession: (sessionId?: string | null) => void;
  submitMembershipForm: (
    skipApiAddressValidation?: boolean,
    skipDonorAddressValidation?: boolean
  ) => Promise<ISubmitNewJoinFormDataResponse>;
  updateCache: (sessionId: string, data: any) => Promise<IUpdateCacheResponse>;
  setStep: (step: string) => void;
  checkMembershipStatus: () => Promise<MembershipStatus | null>;
  getPaymentManifestId: (softError?: ErrorCode) => void;
  getConfirmation: () => void;
  registerIdentityAccount: (sessionId: string, email: string, password: string) => Promise<IRegisterAccountResponse>;
  sendTag: (type: UtagType, page: string, overrides?: object) => void;
  routeMembershipStatus: (status: string) => Promise<void>;
  updateAssociates: (modification: 'add' | 'update' | 'remove', associate: AssociateInfo) => Promise<void>;
  updateProducts: (modification: 'add' | 'remove', product: ProductType) => void;
  updateEnrollments: (modification: 'add' | 'remove', enrollment: Enrollments) => void;
  updateBillPlan: (updatedBillPlan: CurrentPayPlan) => Promise<void>;
  updateMembershipForm: (formData: NewMembershipFormData) => void;
  getNewBusinessCost: (updatedFormData?: NewMembershipFormData) => Promise<IGetNewBusinessCostResponse>;
  getMembershipContent: (newJoinSessionId: string) => Promise<IGetMembershipContentResponse>;
  handleZipCodeChange: (isOutOfACETerritory?: boolean) => void;
  updatePaperlessOption: (paperlessOptOutType: PaperlessOptOutType, value: boolean) => void;
  updateMembershipSession: (sessionData: NewJoinSessionData) => void;
  fetchPartnerOffers: (sessionId: string, email: string) => Promise<IPartnerIntegration[]>;
  sleep: (ms: number) => Promise<any>;
}

export const MembershipAppStateInitializer: IMembershipAppState = {
  loadingInfo: { isLoading: true, loadingDetails: ['Initializing...'] },
  sessionData: getDefaultFormData(),
  isPriceCartLoading: false,
  loadingProvider: {
    addLoadingDetail: (message: string) => {},
    removeLoadingDetail: (message: string) => {},
    startLoading: (message: string) => {},
    endLoading: () => {},
  },
  membershipInfo: null,
  setZipCouponCampaignCodeAndFlowType: (zipCode, couponToken, campaignCode, flowType, queryString, employeeId) => {},
  refreshSession: (sessionId?: string | null) => {},
  submitMembershipForm: async () => {
    return {
      isActiveMember: false,
      isOutOfACETerritory: false,
      isClubCodeMismatch: false,
      addressValidation: {
        status: '',
        correctedAddress: {
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          zip: '',
          zipSuffix: '',
        },
      },
    };
  },
  updateCache: async (sessionId: string, data: any) => {
    return {
      message: '',
    };
  },
  getPaymentManifestId: () => {},
  setStep: (step) => {},
  checkMembershipStatus: async () => {
    return null;
  },
  getConfirmation: async () => {},
  registerIdentityAccount: async (sessionId: string, email: string, password: string) => {
    return { success: false, message: 'Unable to register account' };
  },
  sendTag: (type, page) => {},
  routeMembershipStatus: (status: string) => {
    return Promise.resolve();
  },
  updateAssociates: (modification: 'add' | 'update' | 'remove', associate: AssociateInfo) => {
    return Promise.resolve();
  },
  updateEnrollments: (modification: 'add' | 'remove', enrollment: Enrollments) => {},
  updateBillPlan: async (updatedBillPlan: CurrentPayPlan) => {},
  updateProducts: (modification: 'add' | 'remove', product: ProductType) => {},
  updateMembershipForm: (formData: NewMembershipFormData) => {},
  getNewBusinessCost: async (updatedFormData?: NewMembershipFormData) => {
    return {
      items: [{ id: '', code: CartProductCode.Unknown, description: '', price: 0 }],
      savings: 0,
      salesTax: 0,
      total: 0,
    };
  },
  getMembershipContent: (newJoinSessionId: string) => {
    return Promise.resolve({
      statusCode: 200,
      statusDescription: '',
      data: {
        greenBoxMessage: { id: '', status: ContentStatus.Staging, shortBoxMessage: '', modalTitle: '', modalBody: '' },
        additionalBenefits: [
          {
            id: '',
            status: ContentStatus.Staging,
            membershipLevel: '',
            shortBenefits: [],
            group1: {
              title: '',
              benefits: [],
            },
            group2: {
              title: '',
              benefits: [],
            },
            group3: {
              title: '',
              benefits: [],
            },
          },
        ],
        disclaimerMessage: {
          id: '',
          status: ContentStatus.Staging,
          sections: [],
          valuePairs: [],
        },
      },
    });
  },
  handleZipCodeChange: (isOutOfACETerritory?: boolean) => {},
  updatePaperlessOption: (paperlessOptOutType: PaperlessOptOutType, value: boolean) => {},
  updateMembershipSession: (sessionData: NewJoinSessionData) => {},
  fetchPartnerOffers: (sessionId: string, email: string) => Promise.resolve([]),
  sleep: (ms: number) => Promise.resolve(),
};

export const MembershipAppStateContext = React.createContext<IMembershipAppState>(MembershipAppStateInitializer);
